import axios from 'axios';

// Determine API base URL based on environment
const API_BASE_URL = window.location.hostname === 'localhost' 
    ? 'http://localhost:8000/api'
    : '/api';

/**
 * Get authentication token from URL query parameters
 * @returns {string|null} Authentication token or null if not found
 */
const getAuthToken = () => {
    const params = new URLSearchParams(window.location.search);
    return params.get('token');
};

/**
 * Get headers with authentication token
 * @returns {object} Headers object with x-token if available
 */
const getHeaders = () => {
    const token = getAuthToken();
    const headers = {};
    
    if (token) {
        headers['x-token'] = token;
    }
    
    return headers;
};


/**
 * Create axios instance with interceptor for adding token to all requests
 */
const apiClient = axios.create({
    baseURL: API_BASE_URL,
});

// Add interceptor to include token in all requests
apiClient.interceptors.request.use(
    (config) => {
        const token = getAuthToken();
        if (token) {
            config.headers['x-token'] = token;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Add interceptor to handle 401 responses
apiClient.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            console.error('Authentication failed. Token is invalid or expired.');
        }
        return Promise.reject(error);
    }
);

/**
 * Upload images for conversion
 * @param {FormData} formData - Form data containing files and options
 * @param {Function} onUploadProgress - Callback for upload progress
 * @returns {Promise} API response
 */
export const uploadImages = async (formData, onUploadProgress) => {
    try {
        const response = await apiClient.post('/convert', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
                ...getHeaders(),
            },
            onUploadProgress,
        });
        return response.data;
    } catch (error) {
        if (error.response?.status === 401) {
            throw new Error('Authentication failed. Token is invalid or expired.');
        }
        throw new Error('Error uploading images: ' + (error.response?.data?.detail || error.message));
    }
};

/**
 * Get download link for a file
 * @param {string} zipId - The session/zip ID
 * @returns {Promise} API response
 */
export const getDownloadLink = async (zipId) => {
    try {
        const response = await apiClient.get(`/download/${zipId}`);
        return response.data;
    } catch (error) {
        throw new Error('Error fetching download link: ' + error.message);
    }
};

/**
 * Get all authentication-related functions
 */
export const authAPI = {
    getToken: getAuthToken,
    getHeaders,
};